import { useState, useRef, useCallback } from "react";

import {
  allComponents,
  jsxToHtml,
  regenerateLayoutChildrenIds,
} from "@/lib/visual-editor/helper";
import { makeGeneratedId } from "@/lib/visual-editor/generated-id";
import { previewMessage } from "@/lib/visual-editor/post-message";

// --- Types ---

interface PendingDrop {
  componentType: string;
  id: string;
  props: Record<string, unknown>;
}

interface UseComponentDragDropParams {
  previewIframeRef: React.RefObject<HTMLIFrameElement | null>;
}

interface UseComponentDragDropReturn {
  // State
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activeItem: any;
  showIconPicker: boolean;
  pendingIconDrop: PendingDrop | null;
  showImagePicker: boolean;
  pendingImageDrop: PendingDrop | null;

  // Drag handlers (for DndContext)
  handleDragStart: (event: unknown) => void;
  handleDragEnd: (event: unknown) => void;
  handleDragMove: (event: unknown) => void;

  // Picker callbacks
  handleIconSelected: (iconName: string) => void;
  handleIconPickerClose: () => void;
  handleImageSelected: (src: string, alt: string) => void;
  handleImageSkip: () => void;
  handleImagePickerClose: () => void;
}

// --- Helpers ---

function extractImagePropsFromDragData(active: Record<string, unknown>): {
  imageSrc: string | undefined;
  imageAlt: string | undefined;
  imageStyle: Record<string, string> | undefined;
} {
  const data = (active?.data as Record<string, unknown>)?.current as
    | Record<string, unknown>
    | undefined;
  return {
    imageSrc: data?.imageSrc as string | undefined,
    imageAlt: data?.imageAlt as string | undefined,
    imageStyle: data?.imageStyle as Record<string, string> | undefined,
  };
}

// --- Hook ---

export function useComponentDragDrop({
  previewIframeRef,
}: UseComponentDragDropParams): UseComponentDragDropReturn {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [activeItem, setActiveItem] = useState<any>(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [pendingIconDrop, setPendingIconDrop] = useState<PendingDrop | null>(
    null,
  );
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [pendingImageDrop, setPendingImageDrop] = useState<PendingDrop | null>(
    null,
  );
  const lastDragPointerRef = useRef<{ x: number; y: number } | null>(null);

  // --- Drag handlers ---

  const handleDragStart = (event: unknown) => {
    const ev = event as { active: { data: { current: { elementType: string } } } };
    const componentType = ev.active.data.current.elementType;
    const component = allComponents.find(
      (c) => c.componentType === componentType,
    );
    setActiveItem(component || ev.active);
  };

  const handleDragEnd = (event: unknown) => {
    const ev = event as {
      over: { id: string } | null;
      active: { data: { current: Record<string, unknown> } };
      delta: { x: number; y: number };
      activatorEvent: { clientX: number; clientY: number } | null;
    };
    const { over, active, delta } = ev;
    try {
      if (!over || over.id !== "iframe-drop-zone") {
        return;
      }

      const componentType = active?.data?.current?.elementType as string;
      if (!componentType) {
        console.warn("No component type found in drag event");
        return;
      }

      if (!previewIframeRef?.current?.contentWindow) {
        console.warn("Preview iframe not available");
        return;
      }

      const iframe = previewIframeRef.current;
      const iframeRect = iframe.getBoundingClientRect();

      // Scale factor: iframe may be visually scaled in responsive mode
      // Map screen pixels to iframe's internal viewport coordinates
      const scaleX = iframe.clientWidth / iframeRect.width;
      const scaleY = iframe.clientHeight / iframeRect.height;

      // Use last known pointer position from handleDragMove (more reliable than
      // activatorEvent + delta, which can be stale if the iframe moved during
      // drag — e.g. TransformWrapper centerOnInit animation on first interaction)
      const fallbackX =
        (ev.activatorEvent?.clientX || 0) + (delta?.x || 0);
      const fallbackY =
        (ev.activatorEvent?.clientY || 0) + (delta?.y || 0);
      const dropMouseX = lastDragPointerRef.current?.x ?? fallbackX;
      const dropMouseY = lastDragPointerRef.current?.y ?? fallbackY;
      lastDragPointerRef.current = null;

      // Calculate position relative to iframe, scaled to iframe's internal coordinates
      const x = Math.max(
        0,
        Math.min(
          (dropMouseX - iframeRect.left) * scaleX,
          iframe.clientWidth - 50,
        ),
      );

      const y = Math.max(
        0,
        Math.min(
          (dropMouseY - iframeRect.top) * scaleY,
          iframe.clientHeight - 50,
        ),
      );

      const generatedId = makeGeneratedId(componentType);

      // Check if this is a section/navigation component (has content from drag data)
      const isSection = !!active?.data?.current?.isSection;
      const sectionContent = active?.data?.current?.content as
        | string
        | undefined;

      if (isSection && sectionContent) {
        // Determine wrapper tag from the JSX content
        const trimmed = sectionContent.trimStart();
        const tagName = trimmed.startsWith("<nav")
          ? "nav"
          : trimmed.startsWith("<footer")
            ? "footer"
            : trimmed.startsWith("<section")
              ? "section"
              : "div";

        previewMessage.addComponent(iframe, componentType, generatedId, {
          x,
          y,
          tagName,
          content: jsxToHtml(sectionContent),
          rawJsx: sectionContent,
          dropDirections: ["top", "bottom"],
        });
        return;
      }

      // Standard component path
      const { imageSrc, imageAlt, imageStyle } =
        extractImagePropsFromDragData(active as Record<string, unknown>);

      const component = allComponents.find(
        (c) => c.componentType === componentType,
      );

      if (!component) {
        console.warn(`Component not found for type: ${componentType}`);
        return;
      }

      // Build props - merge default props with image-specific props, style override, and position
      const propsWithPosition = {
        ...component.defaultProps,
        ...(imageSrc && { src: imageSrc }),
        ...(imageAlt && { alt: imageAlt }),
        ...(imageStyle && {
          style: {
            ...(component.defaultProps?.style as
              | Record<string, string>
              | undefined),
            ...imageStyle,
          },
        }),
        x,
        y,
      };

      regenerateLayoutChildrenIds(propsWithPosition);

      // Icon components: show picker before adding to canvas
      if (componentType === "icon") {
        setPendingIconDrop({
          componentType,
          id: generatedId,
          props: propsWithPosition,
        });
        setShowIconPicker(true);
        return;
      }

      // Image/video/audio component from palette (no imageSrc): show picker
      if (
        (componentType === "image" ||
          componentType === "video" ||
          componentType === "audio") &&
        !imageSrc
      ) {
        setPendingImageDrop({
          componentType,
          id: generatedId,
          props: propsWithPosition,
        });
        setShowImagePicker(true);
        return;
      }

      previewMessage.addComponent(
        iframe,
        componentType,
        generatedId,
        propsWithPosition,
      );
    } catch (error) {
      console.error("Error handling drag end:", error);
    } finally {
      setActiveItem(null);
    }
  };

  const handleDragMove = (event: unknown) => {
    const ev = event as {
      over: { id: string } | null;
      active: { data: { current: Record<string, unknown> } };
      delta: { x: number; y: number };
      activatorEvent: { clientX: number; clientY: number } | null;
    };
    const { over, active, delta } = ev;
    if (!over || over.id !== "iframe-drop-zone") {
      return;
    }

    const componentType = active?.data?.current?.elementType as string;
    if (!componentType) {
      console.warn("No component type found in drag event");
      return;
    }

    if (!previewIframeRef?.current?.contentWindow) {
      console.warn("Preview iframe not available");
      return;
    }

    const iframe = previewIframeRef.current;
    const iframeRect = iframe.getBoundingClientRect();

    // Scale factor: iframe may be visually scaled in responsive mode
    const scaleX = iframe.clientWidth / iframeRect.width;
    const scaleY = iframe.clientHeight / iframeRect.height;

    // Reconstruct current cursor position from activator event + delta
    const currentMouseX =
      (ev.activatorEvent?.clientX || 0) + (delta?.x || 0);
    const currentMouseY =
      (ev.activatorEvent?.clientY || 0) + (delta?.y || 0);

    // Store for handleDragEnd (avoids stale activatorEvent when iframe moves during drag)
    lastDragPointerRef.current = { x: currentMouseX, y: currentMouseY };

    const x = Math.max(
      0,
      Math.min(
        (currentMouseX - iframeRect.left) * scaleX,
        iframe.clientWidth,
      ),
    );

    const y = Math.max(
      0,
      Math.min(
        (currentMouseY - iframeRect.top) * scaleY,
        iframe.clientHeight,
      ),
    );

    const generatedId = makeGeneratedId(componentType);

    // Section/navigation: send minimal props with dropDirections
    const isSection = !!active?.data?.current?.isSection;
    if (isSection) {
      previewMessage.dragMove(iframe, componentType, generatedId, {
        x,
        y,
        dropDirections: ["top", "bottom"],
      });
      return;
    }

    // Standard component path
    const { imageSrc, imageAlt } = extractImagePropsFromDragData(
      active as Record<string, unknown>,
    );

    const component = allComponents.find(
      (c) => c.componentType === componentType,
    );

    if (!component) {
      console.warn(`Component not found for type: ${componentType}`);
      return;
    }

    // Build props - merge default props with image-specific props and position
    const propsWithPosition = {
      ...component.defaultProps,
      ...(imageSrc && { src: imageSrc }),
      ...(imageAlt && { alt: imageAlt }),
      x,
      y,
    };

    // Send drag-move with x, y at top level for drop target highlighting
    previewMessage.dragMove(
      iframe,
      componentType,
      generatedId,
      propsWithPosition,
    );
  };

  // --- Picker callbacks ---

  const handleIconSelected = useCallback(
    (iconName: string) => {
      if (!pendingIconDrop || !previewIframeRef?.current) return;
      const props = {
        ...pendingIconDrop.props,
        "data-icon-name": iconName,
      };
      previewMessage.addComponent(
        previewIframeRef.current,
        pendingIconDrop.componentType,
        pendingIconDrop.id,
        props,
      );
      setShowIconPicker(false);
      setPendingIconDrop(null);
    },
    [pendingIconDrop, previewIframeRef],
  );

  const handleIconPickerClose = useCallback(() => {
    setShowIconPicker(false);
    setPendingIconDrop(null);
  }, []);

  const handleImageSelected = useCallback(
    (src: string, alt: string) => {
      if (!pendingImageDrop || !previewIframeRef?.current) return;
      const props = { ...pendingImageDrop.props, src, alt };
      previewMessage.addComponent(
        previewIframeRef.current,
        pendingImageDrop.componentType,
        pendingImageDrop.id,
        props,
      );
      setShowImagePicker(false);
      setPendingImageDrop(null);
    },
    [pendingImageDrop, previewIframeRef],
  );

  const handleImageSkip = useCallback(() => {
    if (!pendingImageDrop || !previewIframeRef?.current) return;
    previewMessage.addComponent(
      previewIframeRef.current,
      pendingImageDrop.componentType,
      pendingImageDrop.id,
      pendingImageDrop.props,
    );
    setShowImagePicker(false);
    setPendingImageDrop(null);
  }, [pendingImageDrop, previewIframeRef]);

  const handleImagePickerClose = useCallback(() => {
    setShowImagePicker(false);
    setPendingImageDrop(null);
  }, []);

  return {
    activeItem,
    showIconPicker,
    pendingIconDrop,
    showImagePicker,
    pendingImageDrop,
    handleDragStart,
    handleDragEnd,
    handleDragMove,
    handleIconSelected,
    handleIconPickerClose,
    handleImageSelected,
    handleImageSkip,
    handleImagePickerClose,
  };
}
